import type { AdminUser, UserStatus } from '@fruuit/shared'
import {
  Badge,
  Button,
  Callout,
  type Column,
  DataTable,
  Input,
  PageHeader,
  Select,
  StatusBadge,
} from '@fruuit/ui'
import { Eye, UserPlus, Users } from 'lucide-react'
import { useState } from 'react'
import { useSearchParams } from 'react-router'
import { errorMessage, isApiError } from '../../api/client'
import { useSession } from '../../auth/session-context'
import { useAdminOrgs, useAdminUsers, useCursorPager } from '../../features/admin-users/api'
import { InviteDialog } from '../../features/admin-users/InviteDialog'
import { UserDrawer } from '../../features/admin-users/UserDrawer'
import { ViewAsDialog, type ViewAsTarget } from '../../features/admin-users/ViewAsDialog'
import { formatDateTime, ROLE_LABELS } from '../../features/auth/format'
import { MfaRequiredNotice } from '../../features/auth/StepUp'
import { useTitle } from '../../lib/useTitle'

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'invited', label: 'Invited' },
  { value: 'deactivated', label: 'Deactivated' },
]

function rolesOf(user: AdminUser): string {
  const roles = user.memberships
    .filter((m) => m.status === 'active')
    .map((m) => `${ROLE_LABELS[m.role] ?? m.role} · ${m.org_name}`)
  return roles.join(', ')
}

/** Admin → Users (P-ADM-007/008): invite, inspect and manage access; operators can "View as". */
export function UsersPage() {
  useTitle('Users')
  const me = useSession()
  const operator = me.platform_role === 'platform_operator' && me.impersonated_by === null
  const [params, setParams] = useSearchParams()
  const orgId = params.get('org') ?? ''
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const pager = useCursorPager()
  const users = useAdminUsers({ org_id: orgId, status, q, limit: 25, cursor: pager.cursor })
  const orgs = useAdminOrgs({ limit: 200 })
  const [inviteOpen, setInviteOpen] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const [viewAs, setViewAs] = useState<ViewAsTarget | null>(null)

  const columns: Column<AdminUser>[] = [
    {
      id: 'name',
      header: 'Person',
      cell: (u) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink">{u.display_name}</p>
          <p className="truncate text-xs text-sage">{u.email}</p>
        </div>
      ),
    },
    {
      id: 'access',
      header: 'Access',
      cell: (u) => (
        <div className="flex max-w-[22rem] flex-wrap gap-1.5">
          {u.platform_role ? <Badge tone="brand">{ROLE_LABELS[u.platform_role]}</Badge> : null}
          {u.memberships
            .filter((m) => m.status === 'active')
            .map((m) => (
              <Badge
                key={`${m.org_id}-${m.role}`}
                tone={m.org_status === 'suspended' ? 'attention' : 'outline'}
              >
                {ROLE_LABELS[m.role] ?? m.role} · {m.org_name}
                {m.org_status === 'suspended' ? ' (suspended)' : ''}
              </Badge>
            ))}
          {u.pending_invites.map((i) => (
            <Badge key={i.id} tone="info">
              Invited · {i.org_name}
            </Badge>
          ))}
          {!u.platform_role && rolesOf(u) === '' && u.pending_invites.length === 0 ? (
            <span className="text-xs text-sage">No access</span>
          ) : null}
        </div>
      ),
    },
    { id: 'status', header: 'Status', cell: (u) => <StatusBadge status={u.status} /> },
    {
      id: 'last',
      header: 'Last sign-in',
      cell: (u) => (
        <span className="whitespace-nowrap text-sage">{formatDateTime(u.last_sign_in_at)}</span>
      ),
    },
    {
      id: 'mfa',
      header: '2FA',
      cell: (u) =>
        u.mfa_enrolled ? <Badge tone="positive">On</Badge> : <span className="text-sage">Off</span>,
    },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      cell: (u) =>
        operator && u.can_impersonate ? (
          <Button
            size="sm"
            variant="secondary"
            icon={<Eye />}
            onClick={() => setViewAs({ id: u.id, display_name: u.display_name, email: u.email })}
          >
            View as
          </Button>
        ) : null,
    },
  ]

  const resetPage = () => pager.reset()

  return (
    <>
      <PageHeader
        eyebrow="Users"
        title="Invite-only access."
        description="Invite people into an organisation, manage their roles, sessions and two-factor sign-in. Operators can view the portal as any active user."
        actions={
          <Button icon={<UserPlus />} onClick={() => setInviteOpen(true)}>
            Invite
          </Button>
        }
      />

      <form
        className="mb-5 grid gap-3 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,0.8fr)_auto] sm:items-end"
        onSubmit={(event) => {
          event.preventDefault()
          setQ(search.trim())
          resetPage()
        }}
      >
        <Input
          label="Search"
          name="q"
          placeholder="Name or email"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Select
          label="Organisation"
          name="org"
          value={orgId}
          placeholder="All organisations"
          options={(orgs.data?.items ?? []).map((o) => ({ value: o.id, label: o.name }))}
          onChange={(event) => {
            const next = new URLSearchParams(params)
            if (event.target.value) next.set('org', event.target.value)
            else next.delete('org')
            setParams(next, { replace: true })
            resetPage()
          }}
        />
        <Select
          label="Status"
          name="status"
          value={status}
          placeholder="Any status"
          options={STATUS_OPTIONS}
          onChange={(event) => {
            setStatus(event.target.value as UserStatus | '')
            resetPage()
          }}
        />
        <Button type="submit" variant="secondary">
          Search
        </Button>
      </form>

      {users.isError ? (
        isApiError(users.error, 'mfa_required') ? (
          <MfaRequiredNotice onVerified={() => void users.refetch()} />
        ) : (
          <Callout tone="danger" role="alert">
            {errorMessage(users.error)}
          </Callout>
        )
      ) : (
        <DataTable
          caption="Users"
          columns={columns}
          rows={users.data?.items ?? []}
          getRowId={(u) => u.id}
          loading={users.isPending}
          onRowClick={(u) => setSelected(u.id)}
          empty={
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <Users aria-hidden="true" className="size-6 text-sage" />
              <p className="font-medium text-ink">No users match these filters</p>
              <p className="text-sm text-sage">Invite someone, or clear the search.</p>
            </div>
          }
          pagination={{
            mode: 'server',
            hasPrevious: pager.hasPrevious,
            hasNext: Boolean(users.data?.next_cursor),
            onPrevious: pager.previous,
            onNext: () => pager.next(users.data?.next_cursor ?? null),
            summary: `Page ${pager.page}`,
          }}
        />
      )}

      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        orgId={orgId || undefined}
      />
      <UserDrawer
        userId={selected}
        onClose={() => setSelected(null)}
        onViewAs={(t) => setViewAs(t)}
      />
      <ViewAsDialog target={viewAs} onClose={() => setViewAs(null)} />
    </>
  )
}
